import { Box, Drop, RadioButtonGroup } from 'grommet';
import { ComponentProps } from 'preact/compat';
import { useMemo, useRef, useState } from 'preact/hooks';

import RoundButton from '@/components/RoundButton';
import { useIsMobileSize } from '@/hooks';
import { useSelector } from '@/redux/helper';
import { Camera } from '@/redux/slices/habitats/types';
import { buildURL } from '@/shared/fetch';

import style from './style.scss';
import switchCamera from './switchCamera.svg';

interface CameraSelectorProps {
  habitatId?: string;
  cameras?: Camera[];
  selectedCamera?: Camera;
  setSelectedCamera?: (camera: Camera) => void;
}

const CameraSelector = ({ habitatId, cameras, selectedCamera, setSelectedCamera }: CameraSelectorProps) => {
  const userId = useSelector((state) => state.user.userId);
  const cameraSelectorRef = useRef<HTMLDivElement>(null);
  const [showSelect, setShowSelect] = useState(false);
  const isMobileSize = useIsMobileSize();

  const needsSelectCamera = (cameras?.length ?? 0) > 1;

  const availableCameras = useMemo(
    () => cameras?.map(({ _id, cameraName }) => ({ label: cameraName, value: _id })),
    [cameras],
  );

  const onChangeHandler: ComponentProps<'input'>['onChange'] = ({ currentTarget }) => {
    const cameraId = currentTarget.value;
    const camera = cameras?.find(({ _id }) => _id === cameraId);
    if (camera) {
      setSelectedCamera?.(camera);
    }
  };

  const currentCameraId = useMemo(() => {
    if (!needsSelectCamera) {
      return undefined;
    }
    const currentCamera = cameras?.find(({ _id }) => _id === selectedCamera?._id);
    return currentCamera?._id || cameras?.[0]._id;
  }, [needsSelectCamera, selectedCamera, cameras]);

  const clickHandler = () => {
    setShowSelect((current) => !current);
    fetch(buildURL('/log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: 'CameraSwitch',
        userId,
        payload: { habitatId },
      }),
    });
  };

  const closeHandler = () => {
    setShowSelect(false);
  };

  if (!needsSelectCamera || !availableCameras || !currentCameraId) {
    return null;
  }

  return (
    <div ref={cameraSelectorRef} className={style.cameraButtonContainer}>
      <Box onClick={clickHandler} onMouseEnter={isMobileSize ? undefined : clickHandler}>
        <RoundButton width={28} backgroundColor="var(--hunterGreenMediumDark)" color="white" className={style.cameraButton}>
          <img src={switchCamera} alt="camera selector" className={style.switchCameraIcon} />
        </RoundButton>
      </Box>
      {cameraSelectorRef.current && showSelect && (
        <Drop
          plain
          responsive
          align={{ bottom: 'bottom' }}
          target={cameraSelectorRef.current}
          onClose={closeHandler}
          onClickOutside={closeHandler}
          onEsc={closeHandler}
          onMouseLeave={closeHandler}
          style={{ marginBottom: '30px', overflow: 'visible' }}
        >
          <Box align="center" pad="15px" className={style.cameraDrop}>
            <div className={style.extension} onClick={() => setShowSelect(!showSelect)} />
            <h3>Switch Camera:</h3>
            <RadioButtonGroup
              name="selectedStream"
              options={availableCameras}
              value={currentCameraId}
              onChange={onChangeHandler}
              className={style.radioContainer}
            />
          </Box>
        </Drop>
      )}
    </div>
  );
};

export default CameraSelector;
